import Styled, { css } from 'styled-components';

import { cssDeclaration } from '../../../Utils';

const ANIMATION_TIMING = '100ms ease-out';

export const ToggleWrap = Styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

export const ToggleButton = Styled.span`
  position: relative;
  display: flex;
  border-radius: 40px;
  align-items: center;
  transition: background-color ${ANIMATION_TIMING};

  ${({ theme: { themeTwo }}) => {
    const { form: { radio: { toggle }}} = themeTwo;
    const height = themeTwo.global.edgeSize[toggle.button.height] || toggle.button.height;
    const width = themeTwo.global.edgeSize[toggle.button.width] || toggle.button.width;
    const margin = themeTwo.global.edgeSize[toggle.button.margin.right] || toggle.button.margin.right;

    return css`
      width: ${width};
      height: ${height};
      margin-left: ${margin};
      /* background-color */ ${cssDeclaration(themeTwo, 'background-color', toggle.button.backgroundColor)};
      /* border */ ${cssDeclaration(themeTwo, 'border', toggle.button.border)};
      /* padding */ ${cssDeclaration(themeTwo, 'padding', toggle.button.padding)};
      ${toggle.button.additionalStyles && toggle.button.additionalStyles}
    `;
  }}
`;

export const ToggleSlidingIndicator = Styled.span`
  position: absolute;
  border-radius: 50%;
  transition: background-color ${ANIMATION_TIMING}, left ${ANIMATION_TIMING};
  top: 50%;
  transform: translateY(-50%);

  ${({ theme: { themeTwo }}) => {
    const { form: { radio: { toggle }}} = themeTwo;
    const size = themeTwo.global.edgeSize[toggle.slidingIndicator.size] || toggle.slidingIndicator.size;
    const toggleButtonPadding = themeTwo.global.edgeSize[toggle.button.padding.horizontal] || toggle.button.padding.horizontal;

    return css`
      width: ${size};
      height: ${size};
      left: ${toggleButtonPadding};
      /* background-color */ ${cssDeclaration(themeTwo, 'background-color', toggle.slidingIndicator.color)};
    `;
  }}
`;

export const ToggleLabel = Styled.label`
  display: flex;
  cursor: pointer;
  position: relative;
  align-items: center;
  transition: color ${ANIMATION_TIMING};

  ${({ theme: { themeTwo }}) => {
    const { form: { radio: { toggle }}} = themeTwo;

    return css`
      line-height: ${toggle.label.lineHeight};
      font-weight: ${toggle.label.weight};

      /* color */ ${cssDeclaration(themeTwo, 'color', toggle.label.color)};
      /* font-size */ ${cssDeclaration(themeTwo, 'font-size', toggle.label.size)};

      &:hover {
        /* color */ ${cssDeclaration(themeTwo, 'color', toggle.state.hover.label.color)};

        & > ${ToggleButton} {
          /* background-color */ ${cssDeclaration(themeTwo, 'background-color', toggle.state.hover.button.backgroundColor)};
          /* border */ ${cssDeclaration(themeTwo, 'border', toggle.state.hover.button.border)};

          & > ${ToggleSlidingIndicator} {
            /* background-color */ ${cssDeclaration(themeTwo, 'background-color', toggle.state.hover.slidingIndicator.color)};
          }
        }
      }
    `;
  }}
`;

export const ToggleInput = Styled.input<{ clickFocus: boolean }>`
  width: 1px;
  height: 1px;
  position: absolute;
  top: 0;
  left: -999px;
  clip: rect(1px, 1px, 1px, 1px);
  overflow: hidden;
  white-space: nowrap;

  ${({ theme: { themeTwo }, clickFocus }) => {
    const { form: { radio: { toggle }}} = themeTwo;
    const { checked, checkedHover, disabled, disabledChecked, focus } = toggle.state;
    const slidingIndicatorSize = themeTwo.global.edgeSize[toggle.slidingIndicator.size] || toggle.slidingIndicator.size;
    const toggleButtonPadding = themeTwo.global.edgeSize[toggle.button.padding.horizontal] || toggle.button.padding.horizontal;

    return css`
      &:checked ~ ${ToggleLabel} {
        /* color */ ${cssDeclaration(themeTwo, 'color', checked.label.color)};

        & > ${ToggleButton} {
          /* background-color */ ${cssDeclaration(themeTwo, 'background-color', checked.button.backgroundColor)};
          /* border */ ${cssDeclaration(themeTwo, 'border', checked.button.border)};

          & > ${ToggleSlidingIndicator} {
            /* background-color */ ${cssDeclaration(themeTwo, 'background-color', checked.slidingIndicator.color)};
            left: calc(100% - ${slidingIndicatorSize} - ${toggleButtonPadding});
          }
        }
      }

      ${checkedHover && css`
        &:hover:checked ~ ${ToggleLabel} {
          /* color */ ${cssDeclaration(themeTwo, 'color', checkedHover.label.color)};

          & > ${ToggleButton} {
            /* background-color */ ${cssDeclaration(themeTwo, 'background-color', checkedHover.button.backgroundColor)};
            /* border */ ${cssDeclaration(themeTwo, 'border', checkedHover.button.border)};

            & > ${ToggleSlidingIndicator} {
              /* background-color */ ${cssDeclaration(themeTwo, 'background-color', checkedHover.slidingIndicator.color)};
              left: calc(100% - ${slidingIndicatorSize} - ${toggleButtonPadding});
            }
          }
        }
      `}

      &:disabled:checked ~ ${ToggleLabel} {
        /* color */ ${cssDeclaration(themeTwo, 'color', disabledChecked.label.color)};

        & > ${ToggleButton} {
          /* background-color */ ${cssDeclaration(themeTwo, 'background-color', disabledChecked.button.backgroundColor)};
          /* border */ ${cssDeclaration(themeTwo, 'border', disabledChecked.button.border)};

          & > ${ToggleSlidingIndicator} {
            /* background-color */ ${cssDeclaration(themeTwo, 'background-color', disabledChecked.slidingIndicator.color)};
          }
        }
      }

      &:disabled ~ ${ToggleLabel} {
        /* color */ ${cssDeclaration(themeTwo, 'color', disabled.label.color)};

        & > ${ToggleButton} {
          /* background-color */ ${cssDeclaration(themeTwo, 'background-color', disabled.button.backgroundColor)};
          /* border */ ${cssDeclaration(themeTwo, 'border', disabled.button.border)};

          & > ${ToggleSlidingIndicator} {
            /* background-color */ ${cssDeclaration(themeTwo, 'background-color', disabled.slidingIndicator.color)};
          }
        }
      }

      ${!clickFocus && css`
        ${toggle.focusPosition === 'indicatorOnly' ?
          css`
            &:focus ~ ${ToggleLabel} > ${ToggleButton} {
              /* box-shadow */ ${cssDeclaration(themeTwo, 'box-shadow', focus.button.shadow)};
            }
          ` :
          css`
            &:focus ~ ${ToggleLabel} {
              /* outline */ ${cssDeclaration(themeTwo, 'outline', focus.label.outline)};
            }
          `
        }
      `}
    `;
  }}
`;
